import {Autocomplete, Button, Grid, Paper, TextField, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {SearchRounded} from "@mui/icons-material";
import {useRecoilState, useSetRecoilState} from "recoil";
import React, {useContext, useEffect, useState} from "react";
import {profileList} from "../state/profileState";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import axios from "axios";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";

interface IProfileSearchProps {
    legalEntities?: any[];
    departments?: any[];
    name?: string | null;
}

const ProfileSearch = () => {
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [profileListLocal, setProfileList] = useRecoilState<any>(profileList);
    const [formValues, setFormValues] = useState<IProfileSearchProps | null>(null);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [departments] = useState<any[]>(globalData.user?.departments);

    const validationSchema = yup.object().shape({
        legalEntities: yup
            .array().min(1, 'message.validation.selectAtLeastOne'),
        departments: yup
            .array().min(1, 'message.validation.selectAtLeastOne'),
        name: yup
            .string().nullable(true).trim().lowercase(),
    });

    const { handleSubmit, formState: {errors}, setValue, control} = useForm<IProfileSearchProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            legalEntities: legalEntities.length === 1 ? legalEntities : [],
            departments: [],
            name: null,
        }
    });

    const findProfiles = ({queryKey}: any) => {
        const [_key, {formValues}] = queryKey;
        return axios.post('/profile/find', formValues);
    }

    const onSuccess = (data: any) => {
        setProfileList(data?.data);
    }

    const onError = (error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const {isFetching, refetch} = useQuery(['findProfiles', {formValues}], findProfiles, {
        enabled: !!formValues,
        onSuccess,
        onError
    })

    const onSubmit = (params: any) => {
        let profileToSearch = {
            legalEntities: legalEntities,
        };
        profileToSearch = {...profileToSearch, ...params};
        setFormValues(profileToSearch);
    };

    useEffect(() => {
        if (sessionStorage.getItem('profileSearch') !== null && formValues === null) {
            let formValuesTmp = JSON.parse(sessionStorage.getItem('profileSearch') as string);
            setValue('legalEntities', formValuesTmp.legalEntities);
            setValue('departments', formValuesTmp.departments);
            setValue('name', formValuesTmp.name);
            setFormValues(formValuesTmp);
            sessionStorage.removeItem('profileSearch');
        }
        if (!!formValues) {
            refetch();
        }
    }, [formValues]);

    useEffect(() => {
        if (formValues !== null) {
            sessionStorage.setItem('profileSearch', JSON.stringify(formValues));
        }
    }, [profileListLocal]);

    return (
        <Paper style={{
            borderRadius: "4px",
            padding: theme.spacing(1) + ' ' + theme.spacing(2),
            marginBottom: theme.spacing(2)
        }}
               elevation={1}
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid container
                      spacing={theme.spacing(2)}
                      alignItems={"end"}
                      justifyContent={'end'}
                >
                    {legalEntities.length > 1 &&
                        <Grid item sm xs={12}>
                            <Controller
                                control={control}
                                name="legalEntities"
                                render={({field: {onChange, value}}: any) => (
                                    <Autocomplete
                                        multiple
                                        id={'legalEntities'}
                                        options={legalEntities}
                                        disableCloseOnSelect={true}
                                        blurOnSelect={false}
                                        value={value || []}
                                        getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.name))}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t("label.legalEntity")}
                                                       error={Boolean(errors.legalEntities)}
                                                       helperText={t(errors.legalEntities?.message as string)}
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            onChange( value);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    <Grid item sm xs={12}>
                        <Controller
                            control={control}
                            name="departments"
                            render={({field: {onChange, value}}: any) => (
                                <Autocomplete
                                    multiple
                                    id={'departments'}
                                    options={departments}
                                    disableCloseOnSelect={true}
                                    blurOnSelect={false}
                                    value={value || []}
                                    getOptionLabel={(option: any) => t('translation:departments.'.concat(option.name))}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t("label.department")}
                                                   error={Boolean(errors.departments)}
                                                   helperText={t(errors.departments?.message as string)}
                                        />
                                    )}
                                    onChange={(event, value) => {
                                        setValue('departments', value);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item sm xs={12}>
                        <Controller
                            control={control}
                            name="name"
                            render={({field: {onChange, value}}: any) => (
                                <TextField
                                    fullWidth
                                    id={"name"}
                                    type={"text"}
                                    label={t("label.name")}
                                    value={value || ''}
                                    onChange={onChange}
                                    error={Boolean(errors.name)}
                                    helperText={t(errors.name?.message as string)}
                                />
                            )}
                        />

                    </Grid>
                    <Grid item>
                        <Button type={'submit'} size={'icon'} color={'accent'}>
                            <SearchRounded/>
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <CustomLoader loading={isFetching}/>
        </Paper>
    );
}

export default ProfileSearch;
